import React, { useState } from 'react';
import {
  ComboBox,
  ComboBoxFilterChangeEvent,
  ComboBoxProps
} from '@progress/kendo-react-dropdowns';
import { filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import { Error, Label } from '@progress/kendo-react-labels';
import { BankItemGroup } from '../../types/game-document/entities/bank-item-group';
import { Dialog } from '../dialogs/dialog';
import YesNoDialog from '../dialogs/yes-no-dialog';
import { StandardInput } from '../forms';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { uuid } from '../../types/common-helper';

const delay: number = 500;

interface BankItemGroupComboBoxProps extends Omit<
  ComboBoxProps,
  'data' | 'value' | 'onChange'
> {
  data: BankItemGroup[];
  value?: string;
  onRemoveBankItemGroup: (index: number) => void;
  onAddBankItemGroup: (group: BankItemGroup) => void;
  onUpdateBankItemGroup?: (index: number, group: BankItemGroup) => void;
  onChange: (id?: string) => void;
  checkUsageCount?: (id: string) => number;
}

interface BankItemGroupDisplay extends BankItemGroup {
  displayName?: string;
}

export const BankItemGroupComboBox = ({
  data: propData,
  value: propValue,
  onRemoveBankItemGroup,
  onAddBankItemGroup,
  onUpdateBankItemGroup,
  onChange,
  checkUsageCount,
  ...restProps
}: BankItemGroupComboBoxProps) => {
  const extendedPropData = React.useMemo(() => {
    return (propData || []).map((group) => ({
      ...group,
      displayName: `${group.name} (${group.maxItemsPerPlayer})`
    }));
  }, [propData]);

  const [data, setData] = useState<BankItemGroupDisplay[]>(extendedPropData);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupMax, setNewGroupMax] = useState<number>(0);
  const [groupToDelete, setGroupToDelete] = useState<number | null>(null);
  const [groupToEdit, setGroupToEdit] = useState<number | null>(null);

  React.useEffect(() => {
    setData(extendedPropData);
  }, [extendedPropData]);

  const filterData = (filter: FilterDescriptor) => {
    return filterBy(extendedPropData, filter);
  };

  const timeout = React.useRef<any>(null);
  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    clearTimeout(timeout.current);
    setFilter(event.filter.value || '');
    setLoading(true);

    timeout.current = setTimeout(() => {
      const filteredData = filterData(event.filter);
      setData(filteredData);
      setLoading(false);
    }, delay);
  };

  const onAddClick = () => {
    setIsDialogOpen(true);
    setNewGroupName(filter);
  };

  const handleDialogAdd = () => {
    if (newGroupName) {
      if (groupToEdit !== null && onUpdateBankItemGroup) {
        const editingGroup = extendedPropData[groupToEdit];
        const updatedGroup: BankItemGroup = {
          ...editingGroup,
          name: newGroupName,
          maxItemsPerPlayer: newGroupMax
        };
        const newDisplayGroup = {
          ...updatedGroup,
          displayName: `${updatedGroup.name} (${updatedGroup.maxItemsPerPlayer})`
        };

        const updatedData = [...data];
        const dataIndex = updatedData.findIndex(
          (g) => g.id === updatedGroup.id
        );
        if (dataIndex > -1) updatedData[dataIndex] = newDisplayGroup;
        setData(updatedData);

        onUpdateBankItemGroup(groupToEdit, updatedGroup);
        setIsDialogOpen(false);
        setGroupToEdit(null);
        setNewGroupName('');
        setNewGroupMax(0);
        return;
      }

      const newGroup: BankItemGroup = {
        id: uuid(),
        name: newGroupName,
        maxItemsPerPlayer: newGroupMax
      };

      const newDisplayGroup = {
        ...newGroup,
        displayName: `${newGroup.name} (${newGroup.maxItemsPerPlayer})`
      };

      setData((prev) => [...prev, newDisplayGroup]);
      onAddBankItemGroup(newGroup);
      setIsDialogOpen(false);
      setFilter('');
      setNewGroupName('');
      setNewGroupMax(0);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setGroupToEdit(null);
    setNewGroupName('');
    setNewGroupMax(0);
  };

  const customItemRender = (li: React.ReactElement, itemProps: any) => {
    const item = itemProps.dataItem as BankItemGroupDisplay;
    const absoluteIndex = extendedPropData.findIndex((g) => g.id === item.id);

    return React.cloneElement(
      li,
      li.props as React.HTMLAttributes<HTMLLIElement>,
      <div
        className={'w-full d-flex justify-content-between align-items-center'}>
        <span>{item.displayName}</span>
        <div className={'d-flex gap-2'}>
          <span
            className={`material-symbols-outlined ${
              itemProps.selected ? '' : 'text-primary'
            }`}
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              setGroupToEdit(absoluteIndex);
              setNewGroupName(item.name);
              setNewGroupMax(item.maxItemsPerPlayer || 0);
              setIsDialogOpen(true);
            }}>
            edit
          </span>
          <span
            className={'material-symbols-outlined text-danger'}
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              setGroupToDelete(absoluteIndex);
            }}>
            delete
          </span>
        </div>
      </div>
    );
  };

  const headerRender = () => {
    if (
      filter &&
      !extendedPropData.some(
        (item) =>
          item.name.toLowerCase() === filter.toLowerCase() ||
          item.displayName?.toLowerCase() === filter.toLowerCase()
      )
    ) {
      return (
        <div
          className={'p-2 border-bottom bg-light'}
          style={{ cursor: 'pointer' }}
          onMouseDown={(e) => {
            e.preventDefault();
            onAddClick();
          }}>
          <strong>Add new group:</strong> {filter}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <ComboBox
        placeholder={'Select or type to add new group'}
        data={data}
        textField={'displayName'}
        dataItemKey={'id'}
        value={
          data.find((i) => i.id === propValue) ||
          extendedPropData.find((i) => i.id === propValue)
        }
        onChange={(e) => onChange(e.target.value?.id)}
        filterable={true}
        onFilterChange={filterChange}
        loading={loading}
        itemRender={customItemRender}
        header={headerRender()}
        listNoDataRender={() => null}
        {...restProps}
      />
      {restProps?.validationMessage && (
        <Error>{restProps.validationMessage}</Error>
      )}

      {isDialogOpen && (
        <Dialog
          title={groupToEdit !== null ? 'Edit Group' : 'Add New Group'}
          onClose={handleDialogClose}>
          <div className="d-flex flex-column gap-3 p-2">
            <StandardInput
              label="Group name"
              type="text"
              name="newGroupName"
              value={newGroupName}
              onChange={(e) =>
                setNewGroupName((e.target.value as string) || '')
              }
              placeholder="e.g. Health Potions"
              autoFocus
            />
            <div className="d-flex flex-column gap-1">
              <Label>Max items per player</Label>
              <NumericTextBox
                name="newGroupMax"
                value={newGroupMax}
                onChange={(e) => setNewGroupMax(e.value || 0)}
                min={0}
              />
              <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                Maximum number of items from this category a single player can hold.
              </span>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button
              themeColor={'primary'}
              onClick={handleDialogAdd}
              disabled={!newGroupName}>
              {groupToEdit !== null ? 'Save' : 'Add'}
            </Button>
          </div>
        </Dialog>
      )}

      {groupToDelete !== null && (
        <YesNoDialog
          title={'Confirm Deletion'}
          onConfirm={(result) => {
            if (result === 'yes') {
              onRemoveBankItemGroup(groupToDelete);
            }
            setGroupToDelete(null);
          }}>
          {(() => {
            const group = data[groupToDelete];
            const count = checkUsageCount ? checkUsageCount(group.id) : 0;
            if (count > 0) {
              return `This group is used by ${count} items. Deleting it will remove it from these items. Are you sure?`;
            }
            return `Are you sure you want to delete this empty group?`;
          })()}
        </YesNoDialog>
      )}
    </>
  );
};

export default BankItemGroupComboBox;
